import { propertyme, settings as settingsApi } from '../api';
import { createPageHeader } from '../components/form';
import { showToast } from '../components/toast';

export async function propertyMePage(): Promise<HTMLElement> {
  const page = document.createElement('div');
  page.appendChild(createPageHeader('PropertyMe Sync'));

  // Load stored username from settings
  let storedUsername = '';
  try {
    const info = await settingsApi.info();
    storedUsername = info.propertyme_username || '';
  } catch { /* proceed without pre-fill */ }

  const card = document.createElement('div');
  card.className = 'card max-w-lg';
  card.innerHTML = `
    <div class="mb-6">
      <div class="flex items-center gap-3 mb-3">
        <div class="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
          <svg class="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">Sync from PropertyMe.com</h3>
          <p class="text-sm text-gray-500">Import properties, payments, and maintenance data</p>
        </div>
      </div>
      <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
        <strong>Note:</strong> Your username is saved securely on the server. Your password is never stored.
      </div>
    </div>

    <div id="pm-form-section">
      <form id="pm-form" class="space-y-4">
        <div>
          <label class="label">PropertyMe Username</label>
          <input name="username" type="text" class="input-field" required placeholder="your@email.com" value="${storedUsername.replace(/"/g, '&quot;')}">
        </div>
        <div>
          <label class="label">PropertyMe Password</label>
          <input name="password" type="password" class="input-field" required>
        </div>
        <button type="submit" class="btn-primary w-full" id="sync-btn">
          Start Sync
        </button>
        <div id="cache-hint" class="hidden text-center">
          <button type="button" id="use-cache-btn" class="text-xs text-gray-400 hover:text-primary-600 underline">
            Use cached data (debug — no login required)
          </button>
        </div>
      </form>
    </div>

    <div id="pm-progress-section" class="hidden">
      <div class="flex items-center gap-2 mb-3">
        <div id="progress-spinner" class="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
        <span id="progress-title" class="font-semibold text-gray-900">Syncing PropertyMe...</span>
      </div>
      <div id="pm-log" class="font-mono text-xs bg-gray-900 text-green-400 rounded-lg p-4 max-h-64 overflow-y-auto space-y-0.5"></div>
    </div>

    <div id="sync-results" class="hidden mt-6 p-4 bg-gray-50 rounded-lg"></div>
  `;
  page.appendChild(card);

  const formSection = card.querySelector('#pm-form-section') as HTMLElement;
  const form = card.querySelector('#pm-form') as HTMLFormElement;
  const cacheHint = card.querySelector('#cache-hint') as HTMLElement;
  const useCacheBtn = card.querySelector('#use-cache-btn') as HTMLButtonElement;
  const progressSection = card.querySelector('#pm-progress-section') as HTMLElement;
  const progressTitle = card.querySelector('#progress-title') as HTMLElement;
  const progressSpinner = card.querySelector('#progress-spinner') as HTMLElement;
  const logPanel = card.querySelector('#pm-log') as HTMLElement;
  const resultsDiv = card.querySelector('#sync-results') as HTMLElement;

  // Show cache button if a cache is likely available (check if username was stored)
  if (storedUsername) {
    cacheHint.classList.remove('hidden');
  }

  function appendLog(msg: string, time?: string): void {
    const line = document.createElement('div');
    const ts = time ? new Date(time).toLocaleTimeString('en-AU', { hour12: false }) : new Date().toLocaleTimeString('en-AU', { hour12: false });
    line.textContent = `${ts}  ${msg}`;
    logPanel.appendChild(line);
    logPanel.scrollTop = logPanel.scrollHeight;
  }

  function showResults(result: NonNullable<Awaited<ReturnType<typeof propertyme.status>>['result']>): void {
    const discHtml = result.discrepancies.length > 0
      ? `<div class="border-t border-gray-200 pt-3 mt-3">
           <h5 class="text-sm font-medium text-yellow-700 mb-2">Discrepancies Found (${result.discrepancies.length})</h5>
           <div class="space-y-2 max-h-48 overflow-y-auto">
             ${result.discrepancies.map((d: unknown) => `<p class="text-xs text-gray-600 bg-gray-50 p-2 rounded">${JSON.stringify(d)}</p>`).join('')}
           </div>
         </div>`
      : '';

    const newRecsHtml = result.new_records.length > 0
      ? `<div class="border-t border-gray-200 pt-3 mt-3">
           <h5 class="text-sm font-medium text-blue-700 mb-2">New Properties Found (${result.new_records.length})</h5>
           <div class="space-y-2 max-h-48 overflow-y-auto">
             ${result.new_records.map((r: unknown) => `<p class="text-xs text-gray-600 bg-gray-50 p-2 rounded">${JSON.stringify(r)}</p>`).join('')}
           </div>
         </div>`
      : '';

    const mergedProps = (result as any).merged_properties || [];
    const mergedHtml = mergedProps.length > 0
      ? `<div class="border-t border-gray-200 pt-3 mt-3">
           <h5 class="text-sm font-medium text-primary-700 mb-2">Same-Address Merges (${mergedProps.length})</h5>
           <div class="space-y-2 max-h-48 overflow-y-auto">
             ${mergedProps.map((m: any) => `<p class="text-xs text-gray-600 bg-gray-50 p-2 rounded">${m.merged_count} PropertyMe entries merged into: ${m.address}</p>`).join('')}
           </div>
         </div>`
      : '';

    resultsDiv.innerHTML = `
      <h4 class="font-semibold text-gray-900 mb-3">Sync Complete</h4>
      <div class="grid grid-cols-3 gap-4 mb-4">
        <div class="text-center">
          <p class="text-2xl font-bold text-primary-600">${result.properties_synced}</p>
          <p class="text-xs text-gray-500">Properties</p>
        </div>
        <div class="text-center">
          <p class="text-2xl font-bold text-green-600">${result.payments_synced}</p>
          <p class="text-xs text-gray-500">Payments</p>
        </div>
        <div class="text-center">
          <p class="text-2xl font-bold text-orange-500">${result.maintenance_synced}</p>
          <p class="text-xs text-gray-500">Maintenance</p>
        </div>
      </div>
      ${mergedHtml}${discHtml}${newRecsHtml}
      ${!mergedHtml && !discHtml && !newRecsHtml ? '<p class="text-sm text-green-600">No discrepancies found.</p>' : ''}
      <button id="sync-again-btn" class="btn-outline w-full mt-4">Sync Again</button>
    `;
    resultsDiv.classList.remove('hidden');

    const syncAgainBtn = resultsDiv.querySelector('#sync-again-btn') as HTMLButtonElement;
    syncAgainBtn.addEventListener('click', () => {
      progressSection.classList.add('hidden');
      resultsDiv.classList.add('hidden');
      logPanel.innerHTML = '';
      formSection.classList.remove('hidden');
    });
  }

  async function startPolling(jobId: string): Promise<void> {
    let logsShown = 0;

    const poll = setInterval(async () => {
      try {
        const status = await propertyme.status(jobId);

        // Append new log entries
        const newLogs = status.logs.slice(logsShown);
        for (const entry of newLogs) {
          appendLog(entry.message, entry.time);
        }
        logsShown = status.logs.length;

        if (status.status === 'complete' && status.result) {
          clearInterval(poll);
          progressSpinner.classList.add('hidden');
          progressTitle.textContent = 'Sync Complete';
          progressTitle.classList.add('text-green-700');
          showResults(status.result);
          showToast('PropertyMe sync completed!', 'success');
          cacheHint.classList.remove('hidden');
        } else if (status.status === 'error') {
          clearInterval(poll);
          progressSpinner.classList.add('hidden');
          progressTitle.textContent = 'Sync Failed';
          progressTitle.classList.add('text-red-700');
          appendLog(`ERROR: ${status.error || 'Unknown error'}`);
          // Show sync-again button
          const retryBtn = document.createElement('button');
          retryBtn.className = 'btn-outline w-full mt-4';
          retryBtn.textContent = 'Try Again';
          retryBtn.addEventListener('click', () => {
            progressSection.classList.add('hidden');
            resultsDiv.classList.add('hidden');
            logPanel.innerHTML = '';
            formSection.classList.remove('hidden');
          });
          progressSection.appendChild(retryBtn);
          showToast(status.error || 'Sync failed', 'error');
        }
      } catch {
        // Network error — keep polling
      }
    }, 2000);
  }

  async function kickOffSync(username: string, password: string, useCache = false): Promise<void> {
    // Save username to settings (fire and forget)
    if (username) {
      settingsApi.update({ propertyme_username: username }).catch(() => {});
    }

    // Switch to progress view
    formSection.classList.add('hidden');
    resultsDiv.classList.add('hidden');
    logPanel.innerHTML = '';
    progressSpinner.classList.remove('hidden');
    progressTitle.textContent = useCache ? 'Replaying from cache...' : 'Syncing PropertyMe...';
    progressTitle.className = 'font-semibold text-gray-900';
    progressSection.classList.remove('hidden');

    try {
      const { job_id } = await propertyme.start({ username, password, use_cache: useCache });
      await startPolling(job_id);
    } catch (err: unknown) {
      progressSection.classList.add('hidden');
      formSection.classList.remove('hidden');
      showToast((err as Error).message || 'Failed to start sync', 'error');
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    await kickOffSync(fd.get('username') as string, fd.get('password') as string);
  });

  useCacheBtn.addEventListener('click', async () => {
    const fd = new FormData(form);
    await kickOffSync(fd.get('username') as string || storedUsername, '', true);
  });

  return page;
}
